import React from 'react'
import { createRoot } from 'react-dom/client'

import * as c from '../common'
import * as r from '../react-utils'

import useMediaQuery from '@mui/material/useMediaQuery'
import {
  Theme as MaterialTheme,
  ThemeProvider,
  createTheme,
} from '@mui/material/styles'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { kebabCase } from 'change-case'
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  matchPath,
  useLocation,
} from 'react-router-dom'

import * as cpm from '../app/components'

import * as cp from './comps'
import CustomerReport, {
  rootSurfaceCustomerReport,
} from './routes/customer-report'
import Customer, { rootSurfaceCustomer } from './routes/customer'
import Customers, { rootSurfaceCustomers } from './routes/customers'
import ItemPrices, { rootSurfaceItemPrices } from './routes/item-prices'
import Login, { rootBGLogin } from './routes/login/Login'
import NotFound, { rootSurfaceNotFound } from './comps/NotFound'
import PrivacyPolicy, {
  rootSurfacePrivacyPolicy,
} from './routes/privacy-policy'
import reportWebVitals from './reportWebVitals'
import { RequireAuth, auth, fireStorage } from './fire'
//@ts-ignore
import gStyles from './global.module.css'
import './global.css'

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    laptop: true
  }
}

c.setLogger(console.log)
c.setLoggerDebug(console.debug)

const ReduxWrapper = r.initProvider({
  fireStorage,
  getUserEmail() {
    return auth.currentUser?.email || null
  },
  getUserID() {
    return auth.currentUser?.uid || null
  },
})

const injectCSSVars = (prefix: string, theme: c.Theme | c.Layer): void => {
  for (const [k, val] of c.entries(theme)) {
    const name = prefix + kebabCase(k)
    if (typeof val === 'object') injectCSSVars(name + '-', val)
    const value = (() => {
      let finalVal = val
      // Too little on web otherwise
      if (k.startsWith('padding')) finalVal = (val as number) * 2
      if (k.startsWith('gap')) finalVal = (val as number) * 2
      return typeof finalVal === 'number'
        ? finalVal.toString() + 'px'
        : finalVal!
    })()
    document.documentElement.style.setProperty(name, value)
  }
}

type UPSRoute = c.r<
  {
    element: React.ReactNode
  } & (
    | {
        index: true
        path?: never
        rootSurface?: never
      }
    | {
        index?: never
        path: string
        rootSurface: c.Surface
      }
  )
>
/**
 * Routes must remain from least specific to most specific ('/customers' before
 * '/customers/:customerID') for reliable location matching. * and index come
 * last.
 */
const ROUTES: readonly UPSRoute[] = [
  {
    element: <PrivacyPolicy />,
    path: 'privacy-policy',
    rootSurface: rootSurfacePrivacyPolicy,
  },
  {
    element: <Login />,
    path: '/login',
    rootSurface: rootBGLogin,
  },
  {
    element: (
      <RequireAuth>
        <ItemPrices />
      </RequireAuth>
    ),
    path: '/item-prices',
    rootSurface: rootSurfaceItemPrices,
  },
  {
    element: (
      <RequireAuth>
        <Customers />
      </RequireAuth>
    ),
    path: '/customers',
    rootSurface: rootSurfaceCustomers,
  },
  {
    element: (
      <RequireAuth>
        <Customer />
      </RequireAuth>
    ),
    path: '/customers/:customerID',
    rootSurface: rootSurfaceCustomer,
  },
  {
    element: (
      <RequireAuth>
        <CustomerReport />
      </RequireAuth>
    ),
    path: '/customers/:customerID/report',
    rootSurface: rootSurfaceCustomerReport,
  },
  // * and index
  {
    element: (
      <RequireAuth>
        <Navigate to="/customers" />
      </RequireAuth>
    ),
    index: true,
  },
  {
    element: <NotFound />,
    path: '*',
    rootSurface: rootSurfaceNotFound,
  },
]

const surfaceToBGClass: c.r<{ [K in c.Surface]: string }> = {
  backdrop: gStyles['bg-backdrop']!,
  canvas: gStyles['bg-canvas']!,
  paper: gStyles['bg-paper']!,
  pasteboard: gStyles['bg-pasteboard']!,
}
const surfaceBGClasses = c.values(surfaceToBGClass)

const Dialogs = React.memo(
  ({ customerID }: { customerID: string | undefined }) => {
    //#region local
    const [payMethodOpen, , payMethodToggle] = r.useSessionVal('payMethodOpen')
    //#endregion

    return (
      <>
        {customerID && (
          <cp.Dialog
            title="Payment Method"
            onClose={payMethodToggle}
            open={!!payMethodOpen}
          >
            <cpm.PayMethod customerID={customerID} />
          </cp.Dialog>
        )}
      </>
    )
  },
)

const AppRoutes = React.memo(() => {
  const location = useLocation()
  const background = location.state && location.state.background

  const customerIDFromPath = matchPath(
    { path: '/customers/:customerID/*' },
    location.pathname,
  )?.params.customerID

  const customerName = c.useSelector((state: c.State) =>
    customerIDFromPath
      ? c.selectCustomer(state, customerIDFromPath).customerName
      : null,
  )

  React.useEffect(() => {
    document.title = customerName
      ? `${customerName} — ${c.DISPLAY_NAME}`
      : c.DISPLAY_NAME
  }, [customerName])

  React.useEffect(() => {
    for (const c of surfaceBGClasses) {
      document.documentElement.classList.remove(c)
      document.body.classList.remove(c)
    }

    // A map would be better suited here but insertion order is not guaranteed.
    const currRoute = ROUTES.find((r) =>
      matchPath(
        {
          // '' would match
          path: r.path || '///',
        },
        location.pathname,
      ),
    )

    if (!currRoute) {
      c.log(`ERR: Route not matched for BG surface change`, ROUTES)
      return
    }

    const { rootSurface } = currRoute

    if (!rootSurface) {
      c.log(`ERR: Matched route has no rootSurface`, currRoute)
      return
    }

    document.documentElement.classList.add(surfaceToBGClass[rootSurface])
    document.body.classList.add(surfaceToBGClass[rootSurface])
  }, [location])

  return (
    <>
      <Routes location={background || location}>
        {ROUTES.map((r) => (
          <Route {...r} key={r.path || 'index'} />
        ))}
      </Routes>

      <Dialogs customerID={customerIDFromPath} />
    </>
  )
})

const App = React.memo(function App() {
  // This is shared across Native and Web
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)')
  // This var is used by MaterialUI
  const [mode = 'light', setMode] = React.useState<'dark' | 'light'>(
    prefersDarkMode ? 'light' : 'dark',
  )
  const t = r.useTheme()

  React.useEffect(() => {
    if (mode === 'dark') {
      injectCSSVars('--ups-', c.dark)
    }
    if (mode === 'light') {
      injectCSSVars('--ups-', c.light)
    }
  }, [mode])

  React.useEffect(() => {
    c.dispatch(c.closersAction())
    c.dispatch(c.settersAction())
  }, [])

  React.useEffect(() => {
    setMode(prefersDarkMode ? 'dark' : 'light')
  }, [prefersDarkMode, setMode])
  React.useEffect(() => {}, [mode])

  const generalMUITheme = React.useMemo(
    (): MaterialTheme =>
      createTheme({
        palette: {
          primary: {
            main: t.backdrop.highlight!,
          },
          // @ts-expect-error
          third: {
            main: '#CC00AA',
          },
        },
        breakpoints: {
          values: r.MUIBreakpoints,
        },
      }),
    [t.backdrop.highlight],
  )

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <ReduxWrapper>
        <BrowserRouter>
          <ThemeProvider theme={generalMUITheme}>
            <AppRoutes />
          </ThemeProvider>
        </BrowserRouter>
      </ReduxWrapper>
    </LocalizationProvider>
  )
})

const root = createRoot(document.getElementById('root') || document.body)
root.render(<App />)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
